// Canonical client timezone handling for the frontend.
//
// Session/note times are stored as timezone-naive wall-clock values that represent the CLIENT'S
// local time. The backend validates them against "now" in the client's timezone. On the frontend we
// therefore never convert these wall-clock values; we only display them and label them with the
// client's zone so a provider in a different timezone knows they are looking at the client's local time.

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEFAULT_TIMEZONE_ID = 'America/New_York';

export interface TimeZoneOption {
	id: string;
	name: string;
}

// US timezones (this is a US-based ABA/DOE org). IANA ids are used so DST is handled automatically.
export const TIMEZONE_OPTIONS: TimeZoneOption[] = [
	{ id: 'America/New_York', name: 'Eastern Time (ET)' },
	{ id: 'America/Chicago', name: 'Central Time (CT)' },
	{ id: 'America/Denver', name: 'Mountain Time (MT)' },
	{ id: 'America/Phoenix', name: 'Arizona (no DST)' },
	{ id: 'America/Los_Angeles', name: 'Pacific Time (PT)' },
	{ id: 'America/Anchorage', name: 'Alaska Time (AKT)' },
	{ id: 'Pacific/Honolulu', name: 'Hawaii Time (HT)' },
	{ id: 'America/Puerto_Rico', name: 'Atlantic Time (AT)' },
];

/** Friendly label for a timezone id, falling back to the raw id if unknown. */
export const getTimeZoneLabel = (timeZoneId?: string | null): string => {
	if (!timeZoneId) return getTimeZoneLabel(DEFAULT_TIMEZONE_ID);

	return TIMEZONE_OPTIONS.find((tz) => tz.id === timeZoneId)?.name ?? timeZoneId;
};

/**
 * Short, DST-aware abbreviation for a timezone id (e.g. "EDT"/"EST"), derived via Intl for the given
 * date. Falls back to the raw id when the runtime cannot resolve the zone.
 */
export const getTimeZoneAbbreviation = (timeZoneId?: string | null, date: Date = new Date()): string => {
	const zone = timeZoneId || DEFAULT_TIMEZONE_ID;

	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: zone,
			timeZoneName: 'short',
		}).formatToParts(date);

		return parts.find((p) => p.type === 'timeZoneName')?.value ?? getTimeZoneLabel(zone);
	} catch {
		return getTimeZoneLabel(zone);
	}
};

// Parse only the stored wall-clock portion. UTC mode prevents the browser's DST rules from
// normalizing a client-local time. Use this holder for wall-clock display, comparison and
// literal serialization, not as an actual UTC instant.
export const parseWallClockTime = (value?: string) => dayjs.utc(value?.slice(0, 19) || '');

export const anchorWallClockTime = (dateTime: string, value: Dayjs | null) =>
	value ? parseWallClockTime(`${dateTime.slice(0, 10)}T${value.format('HH:mm:ss')}`) : null;

export const getWallClockTimeZoneAbbreviation = (timeZoneId: string | null | undefined, wallClockTime: string): string => {
	const zone = timeZoneId || DEFAULT_TIMEZONE_ID;
	try {
		const wallClock = parseWallClockTime(wallClockTime).format('YYYY-MM-DDTHH:mm:ss');
		const instant = dayjs.tz(wallClock, zone);
		// A naive timestamp cannot identify an occurrence of a repeated DST hour, or an
		// instant in a skipped hour. Label the zone without guessing a DST abbreviation.
		if (instant.format('YYYY-MM-DDTHH:mm:ss') !== wallClock ||
			[-1, 1].some((hours) => instant.add(hours, 'hour').tz(zone).format('YYYY-MM-DDTHH:mm:ss') === wallClock)) {
			return getTimeZoneLabel(zone).match(/\((\w+)\)$/)?.[1] ?? getTimeZoneLabel(zone);
		}
		return getTimeZoneAbbreviation(zone, instant.toDate());
	} catch {
		return getTimeZoneLabel(zone);
	}
};

/**
 * Convert a browser-local Date/Dayjs to the client's timezone and return an ISO-style naive
 * datetime string (`YYYY-MM-DDTHH:mm`). Uses Intl.DateTimeFormat for DST-correct conversion.
 *
 * When the provider's browser timezone differs from the client's, the same wall-clock instant
 * maps to a different local time. This function extracts the wall-clock time in the client's
 * zone so the backend receives and stores a value that represents the client's local time.
 */
export const toClientDateTime = (browserTime: Date, clientTimeZoneId?: string | null): string => {
	const zone = clientTimeZoneId || DEFAULT_TIMEZONE_ID;

	try {
		const parts = new Intl.DateTimeFormat('en-CA', {
			timeZone: zone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		}).formatToParts(browserTime);

		const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
		const hour = get('hour') === '24' ? '00' : get('hour');

		return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
	} catch {
		// Fallback: if the zone is unrecognizable, keep browser-local (same as before this fix)
		const d = browserTime;
		const pad = (n: number) => n.toString().padStart(2, '0');
		return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
	}
};

/** Date-only variant: returns `YYYY-MM-DD` in the client's timezone. */
export const toClientDate = (browserTime: Date, clientTimeZoneId?: string | null): string =>
	toClientDateTime(browserTime, clientTimeZoneId).split('T')[0];
